import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, Settings2, Pencil, Trash2, Check, X, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const norm = (s) => String(s || "").split(/\s+/).filter(Boolean).join(" ").toLocaleLowerCase("tr");
const errMsg = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);

function TemplateRow({ kind, t, onChanged, onPick }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(t.ad);
  const save = async () => {
    try {
      await api.put(`/field-templates/${kind}/${t.id}`, { ad: val });
      toast.success("Şablon güncellendi");
      setEdit(false);
      onChanged();
    } catch (e) { toast.error(errMsg(e, "Güncellenemedi")); }
  };
  const del = async () => {
    if (!window.confirm(`"${t.ad}" şablonu silinsin mi?`)) return;
    try {
      await api.delete(`/field-templates/${kind}/${t.id}`);
      toast.success("Şablon silindi");
      onChanged();
    } catch (e) { toast.error(errMsg(e, "Silinemedi")); }
  };
  if (edit) {
    return (
      <div className="flex items-center gap-1 py-1">
        <input value={val} onChange={(e) => setVal(e.target.value)} autoFocus maxLength={300}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
          className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`tpl-${kind}-edit-input-${t.id}`} />
        <button type="button" onClick={save} className="p-1 text-emerald-700 hover:bg-emerald-50 rounded" data-testid={`tpl-${kind}-save-${t.id}`}><Check size={13} /></button>
        <button type="button" onClick={() => { setEdit(false); setVal(t.ad); }} className="p-1 text-slate-500 hover:bg-slate-100 rounded"><X size={13} /></button>
      </div>
    );
  }
  return (
    <div className="group flex items-center gap-1 py-0.5">
      <button type="button" onClick={() => onPick(t.ad)} className="flex-1 text-left text-xs text-slate-800 px-1.5 py-1 rounded hover:bg-slate-100 truncate"
        title="Forma yaz" data-testid={`tpl-${kind}-pick-${t.id}`}>{t.ad}</button>
      <button type="button" onClick={() => setEdit(true)} className="p-1 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded" data-testid={`tpl-${kind}-edit-${t.id}`}><Pencil size={12} /></button>
      <button type="button" onClick={del} className="p-1 text-slate-400 hover:text-rose-700 hover:bg-rose-50 rounded" data-testid={`tpl-${kind}-delete-${t.id}`}><Trash2 size={12} /></button>
    </div>
  );
}

export function TemplateInput({ kind, label, value, onChange, testid }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [newAd, setNewAd] = useState("");
  const load = () => api.get(`/field-templates/${kind}`).then((r) => setList(r.data.rows || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const inList = list.some((t) => norm(t.ad) === norm(value));
  const add = async (ad) => {
    try {
      await api.post(`/field-templates/${kind}`, { ad });
      toast.success("Şablona eklendi");
      setNewAd("");
      load();
    } catch (e) { toast.error(errMsg(e, "Eklenemedi")); }
  };
  const filtered = list.filter((t) => norm(t.ad).includes(norm(q)));

  return (
    <div>
      <div className="flex gap-1">
        <input list={`tpl-options-${kind}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} maxLength={1000}
          placeholder="Yazın veya listeden seçin" data-testid={testid}
          className="w-full min-w-0 px-2 py-1.5 border border-slate-300 rounded text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none" />
        <datalist id={`tpl-options-${kind}`}>
          {list.map((t) => <option key={t.id} value={t.ad} />)}
        </datalist>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" title={`${label} şablonları`} data-testid={`tpl-${kind}-manage-btn`}
              className="shrink-0 w-8 grid place-items-center border border-slate-300 rounded-md text-slate-600 hover:bg-slate-100 transition-colors">
              <Settings2 size={14} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-3" data-testid={`tpl-${kind}-panel`}>
            <div className="text-xs font-bold text-slate-900 mb-2">{label} Şablonları · {list.length}</div>
            <div className="flex gap-1 mb-2">
              <input value={newAd} onChange={(e) => setNewAd(e.target.value)} placeholder="Yeni şablon adı" maxLength={300}
                onKeyDown={(e) => { if (e.key === "Enter" && newAd.trim()) { e.preventDefault(); add(newAd); } }}
                className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`tpl-${kind}-new-input`} />
              <button type="button" disabled={!newAd.trim()} onClick={() => add(newAd)} data-testid={`tpl-${kind}-add-btn`}
                className="inline-flex items-center gap-1 px-2 py-1 bg-slate-900 text-white rounded text-xs font-semibold disabled:opacity-40">
                <Plus size={12} /> Ekle
              </button>
            </div>
            {list.length > 6 && (
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara..." data-testid={`tpl-${kind}-search`}
                className="w-full px-2 py-1 border border-slate-200 rounded text-xs mb-1" />
            )}
            <div className="max-h-60 overflow-y-auto divide-y divide-slate-50">
              {filtered.length === 0 && <div className="text-xs text-slate-400 py-2">Şablon yok</div>}
              {filtered.map((t) => <TemplateRow key={t.id} kind={kind} t={t} onChanged={load} onPick={onChange} />)}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {String(value || "").trim() && !inList && (
        <button type="button" onClick={() => add(value)} data-testid={`tpl-${kind}-save-current`}
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900">
          <BookmarkPlus size={12} /> Şablona ekle
        </button>
      )}
    </div>
  );
}
